<script setup lang="ts">
/**
 * ModalAlertDialog - Universal modal dialog for important notifications
 *
 * Displays modal alerts from the queue with navigation support.
 * Supports different alert types: testing_ready, status_lowered, etc.
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useModalAlerts } from '@/composables/useModalAlerts'

const router = useRouter()
const {
  currentAlert,
  currentIndex,
  totalAlerts,
  hasAlerts,
  canGoNext,
  canGoPrev,
  goNext,
  goPrev,
  dismissCurrent,
} = useModalAlerts()

// Configure appearance based on alert type
const alertConfig = computed(() => {
  const alert = currentAlert.value
  if (!alert) return null

  const alertType = (alert.data?.alertType as string) || alert.type

  switch (alertType) {
    case 'testing_ready':
      return {
        icon: 'mdi-shield-check',
        iconColor: 'info',
        title: 'Задача готова к тестированию',
      }
    case 'status_lowered':
      return {
        icon: 'mdi-alert-circle',
        iconColor: 'warning',
        title: 'Статус задачи понижен',
      }
    default:
      return {
        icon: 'mdi-bell',
        iconColor: 'primary',
        title: alert.title,
      }
  }
})

// Format status labels
const statusLabels: Record<string, string> = {
  draft: 'Черновик',
  pending: 'Ожидает',
  in_progress: 'В работе',
  testing: 'Тестирование',
  completed: 'Завершена',
  on_hold: 'Приостановлена',
  blocked: 'Заблокирована',
  cancelled: 'Отменена',
}

function getStatusLabel(status: string): string {
  return statusLabels[status] || status
}

function openEntity() {
  const alert = currentAlert.value
  if (!alert?.entityId) return

  if (alert.entityType === 'task') {
    router.push({ name: 'task-detail', params: { id: alert.entityId } })
  }

  dismissCurrent()
}
</script>

<template>
  <v-dialog :model-value="hasAlerts" max-width="500" persistent>
    <v-card v-if="currentAlert && alertConfig">
      <v-card-title class="d-flex align-center">
        <v-icon :color="alertConfig.iconColor" class="mr-2">
          {{ alertConfig.icon }}
        </v-icon>
        <span class="flex-grow-1">{{ alertConfig.title }}</span>

        <!-- Navigation counter -->
        <span v-if="totalAlerts > 1" class="text-caption text-grey ml-2">
          {{ currentIndex + 1 }} из {{ totalAlerts }}
        </span>
      </v-card-title>

      <v-card-text>
        <!-- Task ID if available -->
        <div v-if="currentAlert.data?.taskHumanId" class="mb-2">
          <strong>{{ currentAlert.data.taskHumanId }}</strong>
        </div>

        <!-- Main content -->
        <div class="text-body-1">{{ currentAlert.body }}</div>

        <!-- Status change details -->
        <div
          v-if="currentAlert.data?.oldStatus && currentAlert.data?.newStatus"
          class="text-caption text-grey mt-2"
        >
          {{ getStatusLabel(currentAlert.data.oldStatus as string) }}
          &rarr;
          {{ getStatusLabel(currentAlert.data.newStatus as string) }}
        </div>

        <!-- Initiator info -->
        <div v-if="currentAlert.data?.initiator" class="text-caption text-grey mt-1">
          Изменил: {{ currentAlert.data.initiator }}
        </div>
      </v-card-text>

      <v-card-actions>
        <!-- Navigation buttons (only if multiple alerts) -->
        <v-btn
          v-if="totalAlerts > 1"
          icon
          size="small"
          :disabled="!canGoPrev"
          @click="goPrev"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-btn
          v-if="totalAlerts > 1"
          icon
          size="small"
          :disabled="!canGoNext"
          @click="goNext"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>

        <v-spacer />

        <v-btn variant="text" @click="dismissCurrent">Понятно</v-btn>

        <v-btn v-if="currentAlert.entityId" color="primary" @click="openEntity">
          <v-icon start>mdi-open-in-new</v-icon>
          Открыть задачу
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
